import mitt from 'mitt'
import type { ZodType } from 'zod'
import type { Events, NotificationConfig, NotificationType, PromptCancellation, PromptConfig, PromptConfirmation, PromptResponse } from '~/types/Reusable'

export default defineNuxtPlugin(() => {
  const emitter = mitt<Events>()
  const notificationShown = ref(false)
  const notificationConfig = ref<NotificationConfig>()
  const promptQueue = ref<PromptConfig<Record<string, never>>[]>([])
  emitter.on('show-prompt', (event) => {
    promptQueue.value.push(event)
  })
  emitter.on('hide-prompt', () => {
    promptQueue.value.shift()
  })
  emitter.on('show-notification', (event) => {
    notificationShown.value = false
    nextTick(() => {
      notificationConfig.value = event
      notificationShown.value = true
    })
  })
  emitter.on('hide-notification', () => {
    notificationShown.value = false
  })

  const showNotification = (type: NotificationType, title: string) => {
    emitter.emit('show-notification', { type: type, timeout: 1500, title: title })
  }
  function createPromise<T>(id: string, cancellation: boolean = true) {
    const promise = new Promise<T>((resolve, reject) => {
      const handler = (event: PromptResponse<T>) => {
        if (event.id === id) {
          if (isPromptCancellation(event) && cancellation) {
            reject(event)
          } else if (isPromptConfirmation(event)) {
            resolve(event.value)
          }
          emitter.off('hide-prompt', handler)
        }
      }
      emitter.on('hide-prompt', handler)
    })
    return promise
  }
  function isPromptConfirmation<T>(event: PromptResponse<T>): event is PromptConfirmation<T> {
    return (<PromptConfirmation<T>> event).value !== undefined
  }
  function isPromptCancellation<T>(event: PromptResponse<T>): event is PromptCancellation<T> {
    return (<PromptCancellation<T>> event).cancelled !== undefined
  }
  function showInputPrompt<T extends string | number>(title: string, label: string, withCancellation: boolean = false, rules?: ZodType, initialValue?: T) {
    const id = crypto.randomUUID()
    const promise = createPromise<T>(id, withCancellation)
    emitter.emit('show-prompt', { type: 'text', id, label, title, rules, initialValue })
    return promise
  }
  function showSelectPrompt<T extends object | number | string, K = keyof T>(title: string, label: string, items: T[], itemTitle: K, rules?: ZodType, initialValue?: T | string) {
    const id = crypto.randomUUID()
    const promise = createPromise<T>(id)
    emitter.emit('show-prompt', { type: 'select', id, label, title, items, itemTitle, rules, initialValue })
    return promise
  }
  function showConfirmationPrompt(question: string, optionalQuestion?: string) {
    const id = crypto.randomUUID()
    const promise = createPromise<{ value: boolean, optionalQuestionEnabled: boolean }>(id, false)
    emitter.emit('show-prompt', { type: 'confirmation', id, question, optionalQuestion, title: '' })
    return promise
  }
  function showProgressPrompt(message: string) {
    const id = crypto.randomUUID()
    emitter.emit('show-prompt', { type: 'progress', id, message, title: '' })
    return {
      resolve: () => {
        emitter.emit('hide-prompt', { id })
      },
    }
  }

  return {
    provide: {
      eventBus: {
        emit: emitter.emit,
        promptQueue: promptQueue,
        notificationShown: notificationShown,
        notificationConfig: notificationConfig,
      },
      notifications: {
        error: (title: string) => showNotification('error', title),
        success: (title: string) => showNotification('success', title),
        warning: (title: string) => showNotification('warning', title),
      },
      prompts: {
        input: showInputPrompt,
        select: showSelectPrompt,
        confirm: showConfirmationPrompt,
        progress: showProgressPrompt,
      },
    },
  }
})
